// Wire-format types for the invitation-based sign-up flow (PRD 19).
// Contract: docs/api-contracts/signup.md.
//
// Two account types share the same endpoints; the discriminator is set
// server-side from the invitation token, never from the client body.

export type AccountType = 'ADMIN' | 'MEDICAL_STAFF';

// Invitation metadata returned by GET /auth/signup/verify.
// FE branches the read-only block on `accountType`.
export interface Invitation {
  accountType: AccountType;
  email: string;
  name: string;
  institution: string;
  // MEDICAL_STAFF-only — null for ADMIN.
  department: string | null;
  // MEDICAL_STAFF-only — null for ADMIN.
  jobRole: string | null;
  // ISO-8601 UTC. Sourced from PRD 21 §5.6 (72 h window).
  expiresAt: string;
}

// GET /admin/auth/signup/terms?token=… — published Terms shown during sign-up (US-SU-05).
// One entry per Terms-Management type that has a PUBLISHED version (≤1 published per type,
// PRD 14). `required` drives mandatory vs optional consent. Auto-reflects new publishes.
export interface SignupTerm {
  // The published version id — what the consent decision is recorded against.
  termVersionId: number;
  // Owning Terms type id (PRD 14). Stable across versions.
  typeId: number;
  name: string;
  // Required ([필수]) ⇒ mandatory at sign-up; false ⇒ optional.
  required: boolean;
  // Stored HTML body, BE-sanitized. Rendered verbatim in the [보기] (View) modal.
  content: string;
}

// One consent decision recorded per displayed Term (US-SU-05). FE sends a decision for
// EVERY displayed term on submit, including Optional non-agreement (`agreed: false`).
export interface SignupConsent {
  termVersionId: number;
  agreed: boolean;
}

// POST /admin/auth/signup/request-otp — body { phone, invitationToken } (phone digits only, ^010\d{8}$).
// `invitationToken` (from the invite link ?token=) lets the BE refuse a phone that differs from the one the
// invitation pre-filled, before any SMS is sent. Optional on the wire; the signup form always sends it.
export interface SendCodeInput {
  phone: string;
  invitationToken?: string;
}

// POST /admin/auth/signup/verify-otp — body { phone, code }. BE marks the phone
// verified server-side (keyed by phone) for the signup window; nothing is returned.
export interface VerifyCodeInput {
  phone: string;
  code: string;
}

// POST /admin/auth/signup — body { token, name, password, confirmPassword, phone, institution, consents }.
// `token` from the invite link ?token=. `confirmPassword` is required (BE checks it).
// `phone` is digits-only on the wire. `consents` records every displayed Term's decision
// (US-SU-05); BE re-validates that all Required terms are agreed.
export interface SignupInput {
  token: string;
  name: string;
  password: string;
  confirmPassword: string;
  phone: string;
  institution: string;
  consents: SignupConsent[];
}

// Wire error.code values — mirror the BE ErrorCode names exactly (the FE keys off these).
export type SignupApiErrorCode =
  | 'SIGNUP_TOKEN_INVALID'
  | 'SIGNUP_ALREADY_COMPLETED'
  | 'PASSWORD_POLICY_VIOLATION'
  | 'PASSWORD_CONFIRM_MISMATCH'
  | 'NAME_INVALID'
  | 'PHONE_INVALID'
  // The submitted phone is already used by ANOTHER admin (BE pre-check existsByPhoneAndIdNot, or
  // the uq_ad_admin_phone unique backstop). Shown as a GENERIC modal that does NOT reveal the phone
  // is taken — public signup must not let a stranger probe which numbers already have an account.
  | 'PHONE_DUPLICATE'
  | 'PHONE_NOT_VERIFIED'
  | 'SIGNUP_PHONE_MISMATCH'
  | 'OTP_INVALID'
  | 'OTP_EXPIRED'
  | 'OTP_RATE_LIMITED'
  | 'SMS_SEND_FAILED'
  | 'REQUIRED_TERMS_NOT_AGREED'
  // FE-synthesized: the request never reached the server (no response). Kept
  // distinct from UNKNOWN so callers can show the network message ("연결이…")
  // only here, and an action-specific message for server-responded errors.
  | 'NETWORK'
  | 'UNKNOWN';

export class SignupApiError extends Error {
  readonly code: SignupApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: SignupApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'SignupApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, SignupApiError.prototype);
  }
}

export interface SignupApi {
  verifyInvitation(token: string): Promise<Invitation>;
  // GET /admin/auth/signup/terms?token=… — published Terms for the consent section (US-SU-05).
  listSignupTerms(token: string): Promise<SignupTerm[]>;
  // request-otp / verify-otp / signup all return 204 No Content — resolve void.
  sendCode(input: SendCodeInput): Promise<void>;
  verifyCode(input: VerifyCodeInput): Promise<void>;
  completeSignup(input: SignupInput): Promise<void>;
}
